import type {
  NotificationPreferences,
  SettingsRecord,
} from '../models/settings.model'

export const SETTINGS_STORAGE_KEYS = {
  ROOT: 'inmeet_settings',
} as const

export const DEFAULT_NOTIFICATION_PREFS: NotificationPreferences = {
  // Chat + calls default OFF — users opt in from the Settings page so a
  // fresh install never auto-rings or pops the floating chat toast.
  'enable-chat-notifications': false,
  'play-sound-chat': false,
  'incoming-calls-sound': false,

  // Join / leave notices ON, as other meeting apps show them; the sound stays
  // opt-in. Off, a fresh account saw no sign anyone had arrived or gone.
  'participant-join-alert': true,
  'participant-leave-alert': true,
  'participant-sound': false,

  'mute-all-sounds': false,
  'do-not-disturb': false,

  // Meeting reminders stay ON by default — losing a scheduled meeting is a
  // worse UX than a silent chat.
  'meeting-reminders': true,
  'meeting-reminder-sound': true,
  'turn-off-reminders': false,
}

export const CALL_RINGTONE_OPTIONS = [
  'Classic Ring',
  'Crystal Bell',
  'Soft Pulse',
  'Ocean Wave',
  'Digital Tone',
] as const

export const MESSAGE_TONE_OPTIONS = [
  'Pop Click',
  'Ping',
  'Echo Drop',
  'Chime',
  'Note Bell',
] as const

/**
 * Translation key for each option's label. The stored value stays the English
 * option name — it is what `inmeet_settings` already holds and what keys
 * CALL_RINGTONE_ASSETS — so only what the dropdown shows is translated. Typed
 * over both option lists: adding an option without a label fails to compile.
 */
export const TONE_LABEL_KEYS: Record<
  (typeof CALL_RINGTONE_OPTIONS)[number] | (typeof MESSAGE_TONE_OPTIONS)[number],
  string
> = {
  'Classic Ring': 'settings.general.tones.classicRing',
  'Crystal Bell': 'settings.general.tones.crystalBell',
  'Soft Pulse': 'settings.general.tones.softPulse',
  'Ocean Wave': 'settings.general.tones.oceanWave',
  'Digital Tone': 'settings.general.tones.digitalTone',
  'Pop Click': 'settings.general.tones.popClick',
  'Ping': 'settings.general.tones.ping',
  'Echo Drop': 'settings.general.tones.echoDrop',
  'Chime': 'settings.general.tones.chime',
  'Note Bell': 'settings.general.tones.noteBell',
}

export const DEFAULT_SETTINGS: SettingsRecord = {
  notifications: DEFAULT_NOTIFICATION_PREFS,
  sounds: {
    callRingtone: CALL_RINGTONE_OPTIONS[0],
    messageTone: MESSAGE_TONE_OPTIONS[0],
  },
}

/** Built-in sound assets — keys match the user-visible option labels. */
export const SOUND_ASSETS = {
  chat: '/audios/digitalstore07-new-message-alert-430414.mp3',
  reminder: '/audios/remaindersound.mp3',
  call: '/audios/mixkit-alert-bells-echo-765join.wav',
  participant: '/audios/mixkit-doorbell-tone-2864end.wav',
} as const

export type SoundChannel = keyof typeof SOUND_ASSETS

/**
 * What each ringtone option actually plays.
 *
 * The options were five labels with nothing behind them: choosing one saved a
 * string, raised a toast saying it had been set, and changed nothing — the
 * incoming-call overlay played one hardcoded file whatever was chosen.
 *
 * Mapped onto the assets that exist rather than inventing five new ones. Any
 * label without an entry falls back to the first, so adding an option to the
 * list can never leave a caller silent.
 */
export const CALL_RINGTONE_ASSETS: Record<string, string> = {
  'Classic Ring': '/audios/original_iphone.mp3',
  'Crystal Bell': '/audios/pwlpl-notification-bell-361840.mp3',
  'Soft Pulse': '/audios/mixkit-doorbell-tone-2864end.wav',
  'Ocean Wave': '/audios/remaindersound.mp3',
  'Digital Tone': '/audios/digitalstore07-new-message-alert-430414.mp3',
}

/** The file for a ringtone label, never undefined. */
export function callRingtoneAsset(label: string | undefined): string {
  return (
    (label && CALL_RINGTONE_ASSETS[label]) ||
    CALL_RINGTONE_ASSETS[CALL_RINGTONE_OPTIONS[0]]
  )
}
